/**
 * Splitting a console run into the two things it actually contains.
 *
 * The backend returns one string: the model's answer, then the deterministic
 * input sections appended beneath it under `## Pre-` headers
 * (`report_inputs.append_deterministic_inputs`). The model's answer is JSON;
 * the appended block is markdown. Rendering the whole thing as one blob is what
 * left the product team reading raw JSON.
 *
 * Everything here is pure so the parsing rules can be tested without a DOM.
 */

/**
 * The first appended section marks where the model's answer ends.
 *
 * Matched at the start of a line so a `## Pre-` occurring inside the model's
 * own prose cannot cut the output early.
 */
const APPENDED_INPUTS_PATTERN = /^## Pre-/m;

/**
 * Split a run into the model's answer and the deterministic inputs beneath it.
 *
 * @param {string} output The full string the backend returned.
 * @returns {{modelText: string, deterministicInputs: string}} `deterministicInputs`
 *   is empty when the run carried none — a session with no deterministic data
 *   appends nothing, which is not an error.
 */
export const splitRunOutput = (output) => {
  const text = typeof output === "string" ? output : "";
  const match = text.match(APPENDED_INPUTS_PATTERN);

  if (!match) return { modelText: text.trim(), deterministicInputs: "" };

  return {
    modelText: text.slice(0, match.index).trim(),
    deterministicInputs: text.slice(match.index).trim()
  };
};

/**
 * Strip a ```json fence, which models add often enough to be worth handling.
 */
const unfence = (text) => {
  const fenced = text.match(/^```(?:json)?\s*\n([\s\S]*?)\n?```$/);
  return fenced ? fenced[1].trim() : text;
};

/**
 * Replace every obfuscated label with the participant's name, everywhere.
 *
 * The model is given labels (`STU-4A2F`) so real names never reach it, and it
 * echoes them back throughout — headlines, quotes, per-student sections. The
 * teacher-facing report hydrates them; the console reads the same run, so it
 * has to as well or the product team is left matching ids to people by hand.
 *
 * Applied to the rendered view only. Raw keeps the labels, because that is the
 * evidence of what the model actually received.
 *
 * @param {*} value Any node of the parsed report.
 * @param {Object<string,string>} namesByLabel `{"STU-4A2F": "Ada Lovelace"}`.
 * @returns {*} The same shape with labels replaced in every string.
 */
export const hydrateParticipantNames = (value, namesByLabel) => {
  const labels = Object.keys(namesByLabel || {});
  if (!labels.length) return value;

  // Longest first: a label that is a prefix of another must not match first.
  const pattern = new RegExp(
    labels.sort((a, b) => b.length - a.length).map(escapeForRegExp).join("|"),
    "g"
  );

  const walk = (node) => {
    if (typeof node === "string") return node.replace(pattern, (m) => namesByLabel[m]);
    if (Array.isArray(node)) return node.map(walk);
    if (node && typeof node === "object") {
      return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, walk(v)]));
    }
    return node;
  };

  return walk(value);
};

const escapeForRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Parse the model's answer into the object the report renderer expects.
 *
 * A model can return prose, a truncated object, or an apology, so failure is a
 * normal outcome rather than a bug. The caller is expected to say so and show
 * the raw text — silently rendering nothing would read as an empty report.
 *
 * @param {string} modelText The model's answer, already split from the inputs.
 * @returns {{report: object|null, error: string|null}} Exactly one is set.
 */
export const parseReportJson = (modelText) => {
  const text = unfence((modelText || "").trim());

  if (!text) return { report: null, error: "The model returned nothing." };

  try {
    const parsed = JSON.parse(text);

    // A bare string or number is valid JSON but not a report; the renderer
    // reads named fields off an object and would show an empty shell.
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { report: null, error: "The model's output is not a report object." };
    }
    return { report: parsed, error: null };
  } catch (e) {
    return { report: null, error: `The model's output is not valid JSON (${e.message}).` };
  }
};

/**
 * The prompt version this renderer was written against.
 *
 * Read for the version signal only. The renderer accepts both the v1/v3 and v9
 * shapes on every field, so this never selects a code path — a payload carrying
 * no `promptStage` is the shape production serves today and is the normal case.
 */
const TARGET_PROMPT_STAGES = ["merged_session_report_v9"];

/**
 * The prompt version the renderer does not know, if any.
 *
 * A v10 is already sitting WIP in the same Google Doc that holds v9, so the
 * next lag is already written. The console warns and still renders everything
 * it recognises; it never refuses (D11).
 *
 * @param {object} report The parsed report.
 * @returns {string|null} The unknown stage, or null when there is nothing to say.
 */
export const unknownPromptStage = (report) => {
  const stage = report?.promptStage;
  if (!stage || typeof stage !== "string") return null;
  return TARGET_PROMPT_STAGES.includes(stage) ? null : stage;
};

/**
 * Every top-level key some section of the renderer reads.
 *
 * Matched the way `findProp` matches — lowercased substring — because that is
 * how the renderer actually resolves keys, so this list answers the real
 * question: would any section have picked this key up?
 */
const CONSUMED_KEY_TERMS = [
  "headline",
  "quote",
  "showcase",
  "student",
  "performance",
  "question",
  "breakdown",
  "math",
  "learning",
  "collaboration",
  "participation",
  "spotlight",
  "metadata",
  "promptstage",
  "safetysignals",
  "nextstep",
];

/**
 * Top-level keys no section of the report consumes.
 *
 * The renderer will always lag Langfuse: prompts are edited in a web UI and
 * published instantly, while the renderer needs a PR and a deploy. A defensive
 * `if (!x) return` makes an unknown key indistinguishable from a legitimately
 * absent one, so a broken contract looks exactly like an empty session — which
 * is precisely how the v9 break reached a real run unnoticed.
 *
 * Keys prefixed `_` are skipped: the contract designates `_taggerDisagreements`
 * as "internal telemetry, never rendered", so the prefix is its own opt-out.
 *
 * Console only (D12). A teacher seeing raw JSON key names would be alarmed and
 * none the wiser.
 *
 * @param {object} report The parsed report.
 * @returns {Array<{key: string, value: *}>} Empty when everything is consumed.
 */
export const unrecognisedKeys = (report) => {
  if (!report || typeof report !== "object") return [];

  return Object.keys(report)
    .filter((key) => !key.startsWith("_"))
    .filter((key) => {
      const lower = key.toLowerCase();
      return !CONSUMED_KEY_TERMS.some((term) => lower.includes(term));
    })
    .map((key) => ({ key, value: report[key] }));
};
